/**
 * CAIP-10 account identifiers: `<namespace>:<reference>:<address>`.
 *
 * A token address means nothing without the chain it lives on — the same
 * twenty bytes are a different asset on every network, and plenty of addresses
 * are deliberately deployed to match across several. CAIP-10 carries both in
 * one string, so a token ad can be pasted, stored and quoted without the chain
 * travelling separately and getting lost.
 *
 * Stored ads keep `{ address, chainId }`, which is what every token ad
 * published so far already looks like. This is the notation people paste and
 * read; the pair remains the storage shape, so nothing written before today
 * needs migrating.
 */

/** The only namespace we speak. Solana and friends would each need their own. */
export const EIP155 = "eip155";

export type SupportedChain = {
  chainId: number;
  /** How a person refers to it, not the chain's formal name. */
  label: string;
  /** CoinGecko's platform id, where it has one — used for metadata lookups. */
  coingeckoPlatform: string | null;
  /**
   * DexScreener's chain slug, where it has one.
   *
   * A second id for the same chain because the two indexes answer different
   * questions: CoinGecko knows a token's aggregate market cap, DexScreener
   * knows a pool's liquidity and knows tokens deployed this week. Neither
   * covers the other, so a chain can appear on one and not the other.
   */
  dexscreenerSlug: string | null;
};

/**
 * Chains a token ad may point at.
 *
 * Deliberately a short list rather than "any EVM chain": every entry has to be
 * one the API can actually read a token from and price, and an ad pointing at a
 * chain nothing can verify is worse than no ad.
 */
export const SUPPORTED_TOKEN_CHAINS: SupportedChain[] = [
  {
    chainId: 1,
    label: "Ethereum",
    coingeckoPlatform: "ethereum",
    dexscreenerSlug: "ethereum",
  },
  {
    chainId: 8453,
    label: "Base",
    coingeckoPlatform: "base",
    dexscreenerSlug: "base",
  },
  {
    chainId: 42161,
    label: "Arbitrum",
    coingeckoPlatform: "arbitrum-one",
    dexscreenerSlug: "arbitrum",
  },
  // Robinhood Chain has no CoinGecko platform id yet, so metadata there comes
  // from the token contract itself rather than a price index.
  {
    chainId: 4663,
    label: "Robinhood",
    coingeckoPlatform: null,
    dexscreenerSlug: null,
  },
];

export const SUPPORTED_TOKEN_CHAIN_IDS: number[] = SUPPORTED_TOKEN_CHAINS.map(
  (c) => c.chainId,
);

export function isSupportedTokenChain(chainId: number): boolean {
  return SUPPORTED_TOKEN_CHAIN_IDS.includes(chainId);
}

export function tokenChain(chainId: number): SupportedChain | undefined {
  return SUPPORTED_TOKEN_CHAINS.find((c) => c.chainId === chainId);
}

export type Caip10 = { namespace: string; chainId: number; address: string };

const EVM_ADDRESS = /^0x[a-fA-F0-9]{40}$/;

/**
 * Parse a CAIP-10 identifier, or return null.
 *
 * Null rather than throwing: the commonest caller is deciding whether a pasted
 * string is one of these at all, and a control-flow exception for "this is
 * ordinary text" would be the wrong shape.
 *
 * Rejects unsupported namespaces and unparseable references, but deliberately
 * NOT unsupported chains — knowing an input is a well-formed identifier for a
 * chain we do not serve is more useful than being told it is gibberish, and
 * lets the caller say so.
 */
export function parseCaip10(value: string): Caip10 | null {
  const parts = value.trim().split(":");
  if (parts.length !== 3) return null;

  // Destructured with defaults rather than asserted: `noUncheckedIndexedAccess`
  // is right about a split's elements being optional, and an empty string
  // fails every check below anyway.
  const [namespace = "", reference = "", address = ""] = parts;
  if (namespace.toLowerCase() !== EIP155) return null;

  const chainId = Number(reference);
  if (!Number.isInteger(chainId) || chainId <= 0) return null;
  if (!EVM_ADDRESS.test(address)) return null;

  return { namespace: EIP155, chainId, address: address.toLowerCase() };
}

/** Render a chain and address as one identifier. */
export function toCaip10(chainId: number, address: string): string {
  return `${EIP155}:${chainId}:${address.toLowerCase()}`;
}

/**
 * A CAIP-19 asset identifier: `<chainId>/<assetNamespace>:<assetReference>`.
 *
 * Note the grammar differs from CAIP-10 by more than a character. An account is
 * `eip155:8453:0x…` — colon-separated throughout — while an asset is
 * `eip155:8453/erc20:0x…`, where the slash separates the chain from the asset
 * and the asset carries its own namespace. They are easy to confuse and not
 * interchangeable: the Farcaster SDK's `swapToken` and `viewToken` both want
 * the asset form, and reject the account one.
 *
 * `address` is emitted as given, so a caller wanting the checksummed form the
 * Farcaster docs use should pass it checksummed. Keeping viem out of this
 * package is deliberate — it is imported by the CDN embed, where an ERC-55
 * implementation would be dead weight.
 */
export function toCaip19(chainId: number, address: string): string {
  return `${EIP155}:${chainId}/erc20:${address}`;
}
